import React, { useState, useEffect } from "react";
import { Dialog } from "../../../../../../ui/components/Dialog";
import { Button } from "../../../../../../ui/components/Button";
import { Select } from "../../../../../../ui/components/Select";
import { TextField } from "../../../../../../ui/components/TextField";
import { FeatherCalendar, FeatherFileText, FeatherX } from "@subframe/core";
import { getClass1Options, getLevelOptions, getLeadLevelId } from "../../../../data/api/registersApi";

interface PeriodLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (period: string, lead: string, leadLevelId: number | null) => void;
  initialPeriod?: string;
  initialLead?: string;
  leadReadOnly?: boolean; // If true, lead is read-only (display only)
}

type LevelNumber = 1 | 2 | 3 | 4 | 5;
type LevelSelections = Partial<Record<LevelNumber, string>>;
type LevelOptionsByLevel = Partial<Record<LevelNumber, string[]>>;

// Helper functions to convert between period formats
// Format used in codebase: "dec-24" (lowercase month abbreviation + "-" + 2-digit year)
// Format for HTML5 month input: "2024-12" (YYYY-MM)

const monthAbbreviations = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec"
];

/**
 * Convert from "dec-24" format to "2024-12" format for HTML5 month input
 */
const periodToMonthInput = (period: string): string => {
  if (!period) return "";
  
  const parts = period.split("-");
  if (parts.length !== 2) return "";
  
  const monthAbbr = parts[0].toLowerCase();
  const yearShort = parts[1];
  
  const monthIndex = monthAbbreviations.indexOf(monthAbbr);
  if (monthIndex === -1) return "";
  
  // Convert 2-digit year to 4-digit year
  // Assuming years 00-50 are 2000-2050, and 51-99 are 1951-1999
  const year = parseInt(yearShort, 10);
  const fullYear = year <= 50 ? 2000 + year : 1900 + year;
  
  // Format as YYYY-MM with zero-padded month
  const month = String(monthIndex + 1).padStart(2, "0");
  return `${fullYear}-${month}`;
};

/**
 * Convert from "2024-12" format to "dec-24" format
 */
const monthInputToPeriod = (monthInput: string): string => {
  if (!monthInput) return "";
  
  const parts = monthInput.split("-");
  if (parts.length !== 2) return "";
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  
  if (isNaN(year) || isNaN(month) || month < 1 || month > 12) return "";
  
  const monthAbbr = monthAbbreviations[month - 1];
  const yearShort = String(year % 100).padStart(2, "0");
  
  return `${monthAbbr}-${yearShort}`;
};

const trimPart = (s: string) => s.trim();

const buildLeadPath = (class1: string, selections: LevelSelections): string => {
  const class1Trimmed = class1.trim();
  if (!class1Trimmed) return "";

  const levels: string[] = [];
  const maxLevel = Math.max(
    0,
    ...Object.keys(selections).map((k) => parseInt(k, 10))
  );
  for (let i = 1; i <= maxLevel; i++) {
    const v = selections[i as LevelNumber];
    if (v) levels.push(v);
  }

  if (levels.length === 0) return "";
  return [class1Trimmed, ...levels].join(" > ");
};

const parseLeadPath = (
  lead: string
): { class1: string; selections: LevelSelections; visibleMaxLevel: LevelNumber } => {
  const parts = lead
    .split(">")
    .map(trimPart)
    .filter((p) => p.length > 0);

  const class1 = parts[0] ?? "";
  const selections: LevelSelections = {};

  // parts[1] is L1, parts[2] is L2, ...
  for (let i = 1; i < parts.length; i++) {
    const levelNumber = i as LevelNumber;
    selections[levelNumber] = parts[i];
  }

  const visibleMaxLevel = (Math.min(
    Math.max(parts.length - 1, 1),
    5
  ) as unknown) as LevelNumber;

  return { class1, selections, visibleMaxLevel };
};

export const PeriodLeadModal: React.FC<PeriodLeadModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialPeriod = "",
  initialLead = "",
  leadReadOnly = false,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>(initialPeriod);
  const [selectedLead, setSelectedLead] = useState<string>(initialLead);
  const [error, setError] = useState<string>("");
  
  // Store month input value in YYYY-MM format for the HTML5 month input
  const [monthInputValue, setMonthInputValue] = useState<string>(
    periodToMonthInput(initialPeriod)
  );

  // Lead selector state
  const [class1Options, setClass1Options] = useState<string[]>([]);
  const [selectedClass1, setSelectedClass1] = useState<string>("");
  const [visibleMaxLevel, setVisibleMaxLevel] = useState<LevelNumber>(1);
  const [levelSelections, setLevelSelections] = useState<LevelSelections>({});
  const [levelOptions, setLevelOptions] = useState<LevelOptionsByLevel>({});
  const [hasNextLevel, setHasNextLevel] = useState<boolean>(false);
  const [isLoadingClass1, setIsLoadingClass1] = useState<boolean>(false);
  const [isLoadingLevels, setIsLoadingLevels] = useState<
    Partial<Record<LevelNumber, boolean>>
  >({});

  const setLevelLoading = (level: LevelNumber, loading: boolean) => {
    setIsLoadingLevels((prev) => ({ ...prev, [level]: loading }));
  };

  const loadClass1 = async () => {
    setIsLoadingClass1(true);
    try {
      const res = await getClass1Options();
      setClass1Options(res.data || []);
    } catch (e: any) {
      console.error("Failed to load class_1 options:", e);
      setClass1Options([]);
    } finally {
      setIsLoadingClass1(false);
    }
  };

  const loadLevelOptions = async (
    levelNumber: LevelNumber,
    class1: string,
    selections: LevelSelections
  ): Promise<string[]> => {
    setLevelLoading(levelNumber, true);
    try {
      const res = await getLevelOptions({
        class1,
        levelNumber,
        l1: selections[1],
        l2: selections[2],
        l3: selections[3],
        l4: selections[4],
        l5: selections[5],
      });
      const opts = (res.data || [])
        .map((x) => x.level_value)
        .filter((v) => typeof v === "string" && v.trim().length > 0);
      setLevelOptions((prev) => ({ ...prev, [levelNumber]: opts }));
      return opts;
    } catch (e: any) {
      console.error(`Failed to load L${levelNumber} options:`, e);
      setLevelOptions((prev) => ({ ...prev, [levelNumber]: [] }));
      return [];
    } finally {
      setLevelLoading(levelNumber, false);
    }
  };

  const resetLeadState = () => {
    setSelectedClass1("");
    setVisibleMaxLevel(1);
    setLevelSelections({});
    setLevelOptions({});
    setHasNextLevel(false);
    setSelectedLead("");
  };

  const computeAndSetLead = (class1: string, selections: LevelSelections) => {
    const lead = buildLeadPath(class1, selections);
    setSelectedLead(lead);
  };

  const preloadHasNextLevel = async (
    currentLevel: LevelNumber,
    class1: string,
    selections: LevelSelections
  ) => {
    const nextLevel = (currentLevel + 1) as LevelNumber;
    if (nextLevel > 5) {
      setHasNextLevel(false);
      return;
    }

    // Only meaningful if current visible level is selected
    if (!selections[currentLevel]) {
      setHasNextLevel(false);
      return;
    }

    const opts = await loadLevelOptions(nextLevel, class1, selections);
    setHasNextLevel(opts.length > 0);
  };

  const handleClass1Change = async (value: string) => {
    setSelectedClass1(value);
    setVisibleMaxLevel(1);
    setLevelSelections({});
    setLevelOptions({});
    setHasNextLevel(false);
    setError("");
    setSelectedLead("");

    if (!value) return;
    // Load L1 options for selected class
    await loadLevelOptions(1, value, {});
  };

  const handleLevelChange = async (level: LevelNumber, value: string) => {
    setError("");

    // Build the updated selections immediately (before state update)
    const nextSelections: LevelSelections = {
      ...levelSelections,
      [level]: value,
    };
    // Clear deeper selections when a parent changes
    for (let i = level + 1; i <= 5; i++) {
      delete nextSelections[i as LevelNumber];
    }

    // Update state
    setLevelSelections(nextSelections);

    // Hide deeper dropdowns when selection changes
    setVisibleMaxLevel(level);

    // Clear deeper options (keep current level options intact)
    setLevelOptions((prev) => {
      const next: LevelOptionsByLevel = { ...prev };
      for (let i = level + 1; i <= 5; i++) {
        delete next[i as LevelNumber];
      }
      return next;
    });

    // Compute lead string immediately based on the new selection state
    computeAndSetLead(selectedClass1, nextSelections);

    // Preload next level options (for showing drill-down button)
    if (selectedClass1 && value) {
      await preloadHasNextLevel(level, selectedClass1, nextSelections);
    } else {
      setHasNextLevel(false);
    }
  };

  const handleDrillDown = async () => {
    const nextLevel = (visibleMaxLevel + 1) as LevelNumber;
    if (nextLevel > 5) return;
    if (!selectedClass1) return;
    if (!levelSelections[visibleMaxLevel]) return;

    // Ensure next level options are loaded (might already be cached by preload)
    if (!levelOptions[nextLevel] || levelOptions[nextLevel]?.length === 0) {
      await loadLevelOptions(nextLevel, selectedClass1, levelSelections);
    }
    setVisibleMaxLevel(nextLevel);
    setHasNextLevel(false); // will be recalculated when the next level is selected
  };

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedPeriod(initialPeriod);
      setSelectedLead(initialLead);
      setMonthInputValue(periodToMonthInput(initialPeriod));
      setError("");

      // If lead is read-only, skip loading lead selection options
      if (leadReadOnly) {
        // Just ensure selectedLead is set from initialLead
        if (initialLead) {
          setSelectedLead(initialLead);
        }
        return;
      }

      // Load class_1 options and prefill from initialLead if present
      (async () => {
        await loadClass1();

        if (initialLead && initialLead.includes(">")) {
          const parsed = parseLeadPath(initialLead);
          if (parsed.class1) {
            setSelectedClass1(parsed.class1);
            setVisibleMaxLevel(parsed.visibleMaxLevel);
            setLevelSelections(parsed.selections);
            computeAndSetLead(parsed.class1, parsed.selections);

            // Load L1 options
            await loadLevelOptions(1, parsed.class1, {});

            // For each deeper level in initialLead, load options so dropdowns render correctly
            for (let lvl = 2; lvl <= parsed.visibleMaxLevel; lvl++) {
              const levelNum = lvl as LevelNumber;
              await loadLevelOptions(levelNum, parsed.class1, parsed.selections);
            }

            // Preload "has next" based on deepest selected
            const deepestSelected = (Math.min(
              parsed.visibleMaxLevel,
              5
            ) as unknown) as LevelNumber;
            await preloadHasNextLevel(deepestSelected, parsed.class1, parsed.selections);
          } else {
            resetLeadState();
          }
        } else {
          // No initial lead → reset
          resetLeadState();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPeriod, initialLead, leadReadOnly]);

  const handleConfirm = async () => {
    if (!selectedPeriod) {
      setError("Please select a period.");
      return;
    }

    // Only validate lead if it's not read-only
    if (!leadReadOnly && !selectedLead) {
      setError("Please select a lead.");
      return;
    }

    // If lead is read-only, use initialLead (should always be present)
    const finalLead = leadReadOnly ? (initialLead || selectedLead) : selectedLead;
    
    if (!finalLead) {
      setError("Lead is required.");
      return;
    }

    // Get level ID from the lead path
    let leadLevelId: number | null = null;
    try {
      leadLevelId = await getLeadLevelId(finalLead);
    } catch (e: any) {
      console.error("Failed to get level ID:", e);
      // Continue anyway - level ID is optional for backward compatibility
    }

    onConfirm(selectedPeriod, finalLead, leadLevelId);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="min-w-[500px] max-w-[600px] w-full">
        {/* Header */}
        <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
          <div className="flex flex-col items-start gap-1">
            <h3 className="text-heading-3 font-heading-3 text-default-font">
              Select Period & Lead
            </h3>
            <p className="text-body font-body text-subtext-color">
              {leadReadOnly 
                ? "Select the period for your pivot table. Lead is from the saved register."
                : "Choose the period and lead for your pivot table"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-neutral-100 text-subtext-color hover:text-default-font transition-colors"
          >
            <FeatherX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-6 p-6">
          {/* Period Selection */}
          <div className="flex flex-col gap-2">
            <TextField
              label="Period"
              helpText="Select year and month"
              icon={<FeatherCalendar />}
            >
              <TextField.Input
                type="month"
                placeholder="Select period"
                value={monthInputValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const monthValue = event.target.value;
                  setMonthInputValue(monthValue);
                  const periodValue = monthInputToPeriod(monthValue);
                  setSelectedPeriod(periodValue);
                  setError("");
                }}
              />
            </TextField>
          </div>

          {/* Lead Selection */}
          <div className="flex flex-col gap-2">
            {leadReadOnly ? (
              // Read-only lead display
              <TextField
                label="Lead"
                helpText="Lead from saved register (cannot be changed)"
                icon={<FeatherFileText />}
              >
                <TextField.Input
                  value={selectedLead || initialLead || ""}
                  readOnly
                  disabled
                  className="bg-neutral-50 cursor-not-allowed"
                />
              </TextField>
            ) : (
              // Full lead selector (existing code)
              <div className="flex flex-col gap-3">
                <Select
                  label="Class (class_1)"
                  placeholder={isLoadingClass1 ? "Loading..." : "Select BS / IS"}
                  value={selectedClass1}
                  onValueChange={handleClass1Change}
                  icon={<FeatherFileText />}
                  disabled={isLoadingClass1}
                >
                  {class1Options.map((c1) => (
                    <Select.Item key={c1} value={c1}>
                      {c1}
                    </Select.Item>
                  ))}
                </Select>

                <Select
                  label="L1"
                  placeholder={
                    !selectedClass1
                      ? "Select class_1 first"
                      : isLoadingLevels[1]
                        ? "Loading..."
                        : "Select L1"
                  }
                  value={levelSelections[1] || ""}
                  onValueChange={(v) => handleLevelChange(1, v)}
                  icon={<FeatherFileText />}
                  disabled={!selectedClass1 || !!isLoadingLevels[1]}
                >
                  {(levelOptions[1] || []).map((opt) => (
                    <Select.Item key={opt} value={opt}>
                      {opt}
                    </Select.Item>
                  ))}
                </Select>

                {visibleMaxLevel >= 2 && (
                  <Select
                    label="L2"
                    placeholder={isLoadingLevels[2] ? "Loading..." : "Select L2 (optional)"}
                    value={levelSelections[2] || ""}
                    onValueChange={(v) => handleLevelChange(2, v)}
                    icon={<FeatherFileText />}
                    disabled={!levelSelections[1] || !!isLoadingLevels[2]}
                  >
                    {(levelOptions[2] || []).map((opt) => (
                      <Select.Item key={opt} value={opt}>
                        {opt}
                      </Select.Item>
                    ))}
                  </Select>
                )}

                {visibleMaxLevel >= 3 && (
                  <Select
                    label="L3"
                    placeholder={isLoadingLevels[3] ? "Loading..." : "Select L3 (optional)"}
                    value={levelSelections[3] || ""}
                    onValueChange={(v) => handleLevelChange(3, v)}
                    icon={<FeatherFileText />}
                    disabled={!levelSelections[2] || !!isLoadingLevels[3]}
                  >
                    {(levelOptions[3] || []).map((opt) => (
                      <Select.Item key={opt} value={opt}>
                        {opt}
                      </Select.Item>
                    ))}
                  </Select>
                )}

                {visibleMaxLevel >= 4 && (
                  <Select
                    label="L4"
                    placeholder={isLoadingLevels[4] ? "Loading..." : "Select L4 (optional)"}
                    value={levelSelections[4] || ""}
                    onValueChange={(v) => handleLevelChange(4, v)}
                    icon={<FeatherFileText />}
                    disabled={!levelSelections[3] || !!isLoadingLevels[4]}
                  >
                    {(levelOptions[4] || []).map((opt) => (
                      <Select.Item key={opt} value={opt}>
                        {opt}
                      </Select.Item>
                    ))}
                  </Select>
                )}

                {visibleMaxLevel >= 5 && (
                  <Select
                    label="L5"
                    placeholder={isLoadingLevels[5] ? "Loading..." : "Select L5 (optional)"}
                    value={levelSelections[5] || ""}
                    onValueChange={(v) => handleLevelChange(5, v)}
                    icon={<FeatherFileText />}
                    disabled={!levelSelections[4] || !!isLoadingLevels[5]}
                  >
                    {(levelOptions[5] || []).map((opt) => (
                      <Select.Item key={opt} value={opt}>
                        {opt}
                      </Select.Item>
                    ))}
                  </Select>
                )}

                {/* Drill-down */}
                {selectedClass1 &&
                  levelSelections[visibleMaxLevel] &&
                  hasNextLevel &&
                  visibleMaxLevel < 5 && (
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-caption font-caption text-subtext-color">
                        You can drill down further.
                      </div>
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        onClick={handleDrillDown}
                        disabled={!!isLoadingLevels[(visibleMaxLevel + 1) as LevelNumber]}
                      >
                        Drill down to L{visibleMaxLevel + 1}
                      </Button>
                    </div>
                  )}

                {/* Selected path preview */}
                {selectedLead && (
                  <div className="text-caption font-caption text-subtext-color">
                    Selected: <span className="text-default-font">{selectedLead}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-3 rounded-md bg-error-50 border border-error-200">
              <p className="text-body font-body text-error-700">{error}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex w-full items-center justify-end gap-2 p-6 border-t border-solid border-neutral-border">
          <Button variant="neutral-tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            onClick={handleConfirm}
            disabled={!selectedPeriod || (!leadReadOnly && !selectedLead)}
          >
            Confirm
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

